import { computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'

/**
 * 바텀시트를 URL(`?sheet=<name>`)에 붙인다.
 *
 * 안드로이드 하드웨어 백이나 iOS 엣지 스와이프에 반응하지 않는 바텀시트는
 * 고장 난 것처럼 느껴진다. 사용자는 시트가 닫히길 기대하는데 화면이 통째로
 * 이전 페이지로 넘어가 버리기 때문이다.
 *
 * 열 때 push, 닫을 때 back — 그래서 뒤로가기가 "시트를 닫고, 그 다음에 이동"이 된다.
 * 캘린더의 일자 시트가 useAgendaQuery 에서 이미 쓰는 방식과 같고,
 * `sheet` 쿼리 키를 이름으로 나눠 쓰므로 화면끼리 충돌하지 않는다.
 *
 * @param {string} name `?sheet=` 에 들어갈 이름 (화면 안에서 유일하면 된다)
 * @returns {{ isOpen: import('vue').WritableComputedRef<boolean>, open: () => void, close: () => void }}
 *   isOpen 은 쓰기 가능하므로 `v-model` 에 그대로 넘길 수 있다.
 */
export function useSheetRoute(name) {
  const route = useRoute()
  const router = useRouter()

  function open() {
    if (route.query.sheet === name) return
    router.push({ query: { ...route.query, sheet: name } }).catch(() => {})
  }

  function close() {
    if (route.query.sheet !== name) return
    // 우리가 쌓은 히스토리 항목(같은 화면 · 시트 없음)이 바로 앞에 있으면 back 으로 되돌린다 —
    // 그래야 뒤로가기 스택에 빈 항목이 남지 않는다.
    // 앞 항목이 다른 화면이면(알림 등에서 ?sheet= 주소로 들어온 경우) back 은 시트가 아니라 화면을 떠나 버린다.
    const back = window.history.state && window.history.state.back
    if (back) {
      const prev = router.resolve(back)
      if (prev.path === route.path && prev.query.sheet !== name) {
        router.back()
        return
      }
    }
    // 딥링크로 시트가 열린 채 진입한 경우엔 되돌아갈 곳이 없다. 쿼리만 지운다.
    const q = { ...route.query }
    delete q.sheet
    router.replace({ query: q }).catch(() => {})
  }

  const isOpen = computed({
    get: () => route.query.sheet === name,
    set: (v) => (v ? open() : close())
  })

  return { isOpen, open, close }
}
